import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { apiRequest, apiUrl } from "../../helpers/api";
import { beginImpersonation } from "../../helpers/impersonation";
import { Skeleton, SkeletonRegion } from "../../components/skeleton/Skeleton";
import { showError, showSuccess } from "../../components/ToastNotifications";
import { useAdminAccess } from "../AdminAccessContext";
import { dateOnly, dateTime, displayName, formatStatus, money, paymentGroupLabels, relativeTime } from "./userFormatting";
import "./AdminManagement.css";
import "./UserQuickView.css";

export const quickViewTabs = [
  ["overview", "Overview", "ti-layout-dashboard"],
  ["personal", "Personal details", "ti-id"],
  ["business", "Business profile", "ti-building-store"],
  ["products", "Products & services", "ti-package"],
  ["subscription", "Subscription & membership", "ti-crown"],
  ["payments", "Payment history", "ti-receipt"],
  ["social", "Social accounts", "ti-share"],
  ["usage", "Usage & limits", "ti-chart-bar"],
  ["activity", "Activity logs", "ti-history"],
  ["security", "Security", "ti-shield-lock"]
];
const tabIds = quickViewTabs.map(([id]) => id);

const parseJson = (value, fallback) => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};
const display = (value) => {
  const parsed = parseJson(value, value);
  if (parsed === null || parsed === undefined || parsed === "") return "—";
  if (Array.isArray(parsed)) return parsed.length ? parsed.join(", ") : "—";
  if (typeof parsed === "boolean") return parsed ? "Yes" : "No";
  if (typeof parsed === "object") return JSON.stringify(parsed);
  return String(parsed);
};
const Detail = ({ label, value, wide }) => <div className={wide ? "is-wide" : undefined}><dt>{label}</dt><dd>{display(value)}</dd></div>;
const Card = ({ title, icon, actions, children, className = "" }) => (
  <article className={`admin-management-card quick-view-card ${className}`}>
    {(title || actions) && <header>{title && <h3>{icon && <i className={`ti ${icon}`} aria-hidden="true" />}{title}</h3>}{actions}</header>}
    {children}
  </article>
);
const Empty = ({ icon = "ti-info-circle", title, children }) => (
  <div className="quick-view-empty"><i className={`ti ${icon}`} aria-hidden="true" /><strong>{title}</strong>{children && <span>{children}</span>}</div>
);
const ErrorNote = ({ message, onRetry }) => (
  <div className="admin-management-error" role="alert">{message} {onRetry && <button type="button" onClick={onRetry}>Retry</button>}</div>
);
const Spinner = () => <span className="admin-management-spinner" aria-hidden="true" />;

// ---------- Overview ----------
const AccountControls = ({ user, onSaved }) => {
  const { can } = useAdminAccess();
  const canStatus = can("admin.users.change_status");
  const canVerify = can("admin.users.verify");
  const [accountStatus, setAccountStatus] = useState(user.accountStatus);
  const [isVerified, setIsVerified] = useState(Boolean(user.isVerified));
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setAccountStatus(user.accountStatus);
    setIsVerified(Boolean(user.isVerified));
  }, [user]);

  const changed = accountStatus !== user.accountStatus || isVerified !== Boolean(user.isVerified);
  const save = async (event) => {
    event.preventDefault();
    const body = {};
    if (canStatus && accountStatus !== user.accountStatus) body.accountStatus = accountStatus;
    if (canVerify && isVerified !== Boolean(user.isVerified)) body.isVerified = isVerified;
    if (!Object.keys(body).length) return;
    const warning = body.accountStatus && body.accountStatus !== "active"
      ? `Set this account to ${body.accountStatus}? The user is signed out of the workspace and can't sign in until reactivated.`
      : "Confirm this account change?";
    if (!window.confirm(warning)) return;
    setSaving(true);
    try {
      await apiRequest(`/api/admin/users/${user.id}`, { method: "PATCH", body: JSON.stringify(body) });
      showSuccess("Account updated.");
      await onSaved();
    } catch (requestError) {
      showError(requestError.message || "Unable to update this account.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Account controls" icon="ti-adjustments">
      <form className="admin-management-form quick-view-form" onSubmit={save}>
        <label>Account status
          <select value={accountStatus} onChange={(event) => setAccountStatus(event.target.value)} disabled={!canStatus || saving}>
            <option value="active">Active</option><option value="inactive">Inactive</option><option value="suspended">Suspended</option>
          </select>
        </label>
        <label className={`admin-management-check${isVerified ? " is-checked" : ""}`}>
          <input type="checkbox" checked={isVerified} onChange={(event) => setIsVerified(event.target.checked)} disabled={!canVerify || saving} />
          <span><strong>Email verified</strong><small>{isVerified ? "Can sign in without confirming email." : "Must confirm email before signing in."}</small></span>
        </label>
        {(canStatus || canVerify) && <div className="admin-management-form-actions">
          <button className="admin-management-button primary" type="submit" disabled={!changed || saving}>{saving ? <><Spinner /> Saving…</> : "Save account changes"}</button>
        </div>}
        {!canStatus && !canVerify && <p className="quick-view-note is-wide">Your role can view this account but not change its status or verification.</p>}
      </form>
    </Card>
  );
};

const OverviewTab = ({ data, latestPayment, canViewPayments, onReload, onOpenTab }) => {
  const { user, business, planUsage, activity } = data;
  const current = data.subscriptions.find((item) => ["active", "past_due", "halted"].includes(item.status));
  const tiles = [
    ["Plan", planUsage?.plan?.name || current?.plan?.name || "Free", current ? `${formatStatus(current.status)} · ${current.billingInterval || ""}` : "No paid subscription", "ti-crown", "subscription"],
    canViewPayments
      ? ["Last payment", latestPayment ? paymentGroupLabels[latestPayment.group] || formatStatus(latestPayment.status) : "None", latestPayment ? `${money(latestPayment.amount, latestPayment.currency)} · ${dateOnly(latestPayment.createdAt)}` : "No checkout attempts", "ti-receipt", "payments"]
      : ["Payments", "Restricted", "Needs “View payments” permission", "ti-lock", "payments"],
    ["Business", business?.brand_name || "Not set up", business ? (Number(business.completed_step) >= 4 ? "Profile complete" : "Setup in progress") : "No business profile", "ti-building-store", "business"],
    ["Last activity", relativeTime(data.lastActivityAt), dateTime(data.lastActivityAt), "ti-activity", "activity"]
  ];
  return (
    <div className="quick-view-stack">
      <div className="quick-view-tiles">
        {tiles.map(([label, value, detail, icon, tab]) => (
          <button type="button" className="quick-view-tile" key={label} onClick={() => onOpenTab(tab)}>
            <i className={`ti ${icon}`} aria-hidden="true" />
            <span>{label}</span><strong>{value}</strong><small>{detail}</small>
          </button>
        ))}
      </div>
      <div className="admin-management-grid">
        <AccountControls user={user} onSaved={onReload} />
        <Card title="Workspace activity" icon="ti-chart-dots">
          <dl className="quick-view-counts">
            {[["Posts", activity.posts], ["Scheduled", activity.scheduledPosts], ["Media", activity.media], ["Leads", activity.leads], ["Automations", activity.automations], ["Support tickets", activity.supportTickets]]
              .map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{new Intl.NumberFormat().format(value || 0)}</dd></div>)}
          </dl>
        </Card>
      </div>
      <Card title="Recent support tickets" icon="ti-lifebuoy">
        {data.supportTickets.length ? <ul className="admin-management-list">{data.supportTickets.slice(0, 5).map((ticket) => (
          <li key={ticket.id}><div><strong>{ticket.ticketNumber} · {ticket.subject}</strong><small>{ticket.priority} priority · {ticket.status}</small></div><time>{dateTime(ticket.updatedAt)}</time></li>
        ))}</ul> : <p>No support tickets.</p>}
      </Card>
    </div>
  );
};

// ---------- Personal details ----------
const profileFields = [["firstName", "First name", 80, true], ["lastName", "Last name", 80, true], ["email", "Email", 254, true], ["phone", "Phone", 40, false], ["designation", "Designation", 255, false]];

const PersonalTab = ({ data, onReload }) => {
  const { can } = useAdminAccess();
  const canEdit = can("admin.users.edit");
  const { user } = data;
  const initial = useCallback(() => Object.fromEntries(profileFields.map(([key]) => [key, user[key] || ""])), [user]);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(initial()); }, [initial]);

  const changed = profileFields.some(([key]) => (form[key] || "").trim() !== (user[key] || ""));
  const save = async (event) => {
    event.preventDefault();
    if (form.email.trim().toLowerCase() !== user.email && !window.confirm("Change this user's sign-in email? They will sign in with the new address.")) return;
    setSaving(true);
    try {
      const body = Object.fromEntries(profileFields
        .filter(([key]) => form[key].trim() !== (user[key] || ""))
        .map(([key, , , required]) => [key, required ? form[key] : form[key].trim() || null]));
      await apiRequest(`/api/admin/users/${user.id}`, { method: "PATCH", body: JSON.stringify(body) });
      showSuccess("Personal details updated.");
      await onReload();
    } catch (requestError) {
      showError(requestError.message || "Unable to update personal details.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-management-grid">
      <Card title="Contact & profile" icon="ti-user">
        <form className="admin-management-form quick-view-form" onSubmit={save}>
          {profileFields.map(([key, label, maxLength, required]) => (
            <label key={key} className={key === "email" ? "is-wide" : undefined}>{label}
              <input name={key} type={key === "email" ? "email" : key === "phone" ? "tel" : "text"} value={form[key]} maxLength={maxLength} required={required}
                onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))} disabled={!canEdit || saving} />
            </label>
          ))}
          {canEdit ? <div className="admin-management-form-actions">
            <button type="button" className="admin-management-button" onClick={() => setForm(initial())} disabled={!changed || saving}>Reset</button>
            <button type="submit" className="admin-management-button primary" disabled={!changed || saving}>{saving ? <><Spinner /> Saving…</> : "Save details"}</button>
          </div> : <p className="quick-view-note is-wide">Your role can't edit personal details.</p>}
        </form>
      </Card>
      <Card title="Account record" icon="ti-database">
        <dl className="admin-management-meta">
          <Detail label="User ID" value={user.id} />
          <Detail label="Username" value={user.username} />
          <Detail label="Registered" value={dateTime(user.createdAt)} />
          <Detail label="Last updated" value={dateTime(user.updatedAt)} />
          <Detail label="Email verification" value={user.isVerified ? "Verified" : "Not verified"} />
        </dl>
      </Card>
    </div>
  );
};

// ---------- Business profile ----------
const businessFields = [
  ["brand_name", "Business name", 120],
  ["website_url", "Website", 255],
  ["industry", "Industry", 120],
  ["brand_tone", "Brand voice", 80],
  ["brand_description", "Business description", 2000],
  ["target_audience", "Target audience", 2000],
  ["customer_pain_points", "Customer pain points", 2000],
  ["products_services", "Products & services summary", 2500],
  ["differentiators", "Differentiators", 2000],
  ["brand_guidelines", "Brand guidelines", 5000]
];
const safeWebsite = (value) => {
  try {
    const url = new URL(value);
    return ["http:", "https:"].includes(url.protocol) ? url.toString() : null;
  } catch {
    return null;
  }
};

const BusinessTab = ({ data, onReload }) => {
  const { can } = useAdminAccess();
  const business = data.business;
  const canEdit = can("admin.businesses.edit");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const startEditing = () => {
    setForm(Object.fromEntries(businessFields.map(([key]) => [key, business[key] || ""])));
    setEditing(true);
  };
  if (!business) return <Empty icon="ti-building-store" title="No business profile">This user hasn't started business onboarding yet.</Empty>;

  const save = async (event) => {
    event.preventDefault();
    // Only edited fields are sent, so values the customer saved (like the exact website text) stay untouched.
    const changes = Object.fromEntries(Object.entries(form).filter(([key, value]) => value.trim() !== (business[key] || "").trim()));
    if (!Object.keys(changes).length) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await apiRequest(`/api/admin/businesses/${business.id}`, { method: "PATCH", body: JSON.stringify(changes) });
      showSuccess("Business profile updated.");
      setEditing(false);
      await onReload();
    } catch (requestError) {
      showError(requestError.message || "Unable to update the business profile.");
    } finally {
      setSaving(false);
    }
  };

  const website = safeWebsite(business.website_url);
  const colors = parseJson(business.colors, []);
  const complete = Number(business.completed_step) >= 4;
  return (
    <div className="quick-view-stack">
      <Card className="quick-view-business-head">
        <div className="quick-view-business-identity">
          {business.logo ? <img src={apiUrl(business.logo)} alt="" /> : <span aria-hidden="true">{(business.brand_name || "B").slice(0, 1).toUpperCase()}</span>}
          <div>
            <h3>{business.brand_name || "Untitled business"}</h3>
            <p>{[business.industry, business.location].filter(Boolean).join(" · ") || "Industry not set"}{website && <> · <a className="admin-inline-link" href={website} target="_blank" rel="noreferrer">{business.website_url}</a></>}</p>
          </div>
          <span className={`admin-status-badge is-${complete ? "active" : "inactive"}`}>{complete ? "Setup complete" : `Setup step ${business.completed_step || 0} of 4`}</span>
        </div>
        {!editing && canEdit && <button type="button" className="admin-management-button" onClick={startEditing}><i className="ti ti-edit" aria-hidden="true" /> Edit profile</button>}
      </Card>
      {editing ? <Card title="Edit business profile" icon="ti-edit">
        <form className="admin-management-form quick-view-form" onSubmit={save}>
          {businessFields.map(([key, label, maxLength]) => (
            <label key={key} className={maxLength > 255 ? "is-wide" : undefined}>{label}
              {maxLength > 255
                ? <textarea name={key} maxLength={maxLength} value={form[key]} onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))} disabled={saving} />
                : <input name={key} maxLength={maxLength} value={form[key]} onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))} disabled={saving} />}
            </label>
          ))}
          <div className="admin-management-form-actions">
            <button type="button" className="admin-management-button" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
            <button type="submit" className="admin-management-button primary" disabled={saving}>{saving ? <><Spinner /> Saving…</> : "Save business profile"}</button>
          </div>
        </form>
      </Card> : <div className="admin-management-grid">
        <Card title="About the business" icon="ti-info-circle"><dl className="admin-management-meta">
          <Detail label="Description" value={business.brand_description} />
          <Detail label="Products & services summary" value={business.products_services} />
          <Detail label="Differentiators" value={business.differentiators} />
          <Detail label="Company size" value={business.company_size} />
        </dl></Card>
        <Card title="Audience" icon="ti-users-group"><dl className="admin-management-meta">
          <Detail label="Target audience" value={business.target_audience} />
          <Detail label="Customer pain points" value={business.customer_pain_points} />
          <Detail label="Age range" value={business.audience_age_range} />
          <Detail label="Locations" value={business.audience_locations} />
          <Detail label="Interests" value={business.audience_interests} />
        </dl></Card>
        <Card title="Contact & location" icon="ti-map-pin"><dl className="admin-management-meta">
          <Detail label="Business phone" value={business.business_phone} />
          <Detail label="Address" value={business.business_address} />
          <Detail label="City / state" value={business.state_city} />
          <Detail label="Country" value={business.country} />
          <Detail label="Location" value={business.location} />
        </dl></Card>
        <Card title="Brand & content preferences" icon="ti-palette"><dl className="admin-management-meta">
          <Detail label="Brand voice" value={business.brand_tone} />
          <Detail label="Brand language" value={business.brand_language} />
          <div><dt>Brand colors</dt><dd>{Array.isArray(colors) && colors.length ? <span className="quick-view-swatches">{colors.map((color) => <span key={color} title={color} style={{ background: color }} />)}</span> : "—"}</dd></div>
          <Detail label="Marketing goals" value={business.content_goals} />
          <Detail label="Custom goal" value={business.custom_goal} />
          <Detail label="Preferred platforms" value={business.preferred_platforms} />
          <Detail label="Preferred content types" value={business.preferred_content_types} />
          <Detail label="Posts per week" value={business.posting_frequency} />
          <Detail label="Logo on images" value={business.logo_enabled ? `${business.logo_placement || "Default"} · ${business.logo_size || "—"}% · ${Math.round((Number(business.logo_opacity) || 0) * 100)}% opacity` : "Off"} />
          <Detail label="Brand guidelines" value={business.brand_guidelines} />
        </dl></Card>
      </div>}
      <p className="quick-view-note">Business profile #{business.id} · created {dateTime(business.createdAt)} · updated {dateTime(business.updatedAt)}. Edits are recorded in Activity logs.</p>
    </div>
  );
};

// ---------- Products & services ----------
const ProductsTab = ({ data }) => {
  const products = parseJson(data.business?.products, []);
  const list = Array.isArray(products) ? products : [];
  if (!data.business) return <Empty icon="ti-package" title="No business profile">Products and services are saved as part of the business profile.</Empty>;
  return (
    <div className="quick-view-stack">
      {list.length ? <div className="admin-management-grid">{list.map((item, index) => (
        <Card key={`${item.name || "item"}-${index}`} title={item.name || item.title || `Item ${index + 1}`} icon={item.type === "service" ? "ti-tools" : "ti-package"}>
          <dl className="admin-management-meta">
            <Detail label="Type" value={item.type === "service" ? "Service" : "Product"} />
            <Detail label="Category" value={item.category} />
            <Detail label="Description" value={item.description} />
            {item.type === "service" ? <>
              <Detail label="Pricing" value={item.pricing} />
              <Detail label="Duration" value={item.duration} />
              <Detail label="Benefits" value={item.benefits} />
            </> : <>
              <Detail label="Price" value={item.price} />
              <Detail label="Features" value={item.features} />
              <Detail label="Images" value={(item.images || (item.imageUrl ? [item.imageUrl] : [])).length || "None"} />
            </>}
          </dl>
        </Card>
      ))}</div> : <Empty icon="ti-package" title="No products or services saved" />}
      {data.business.products_services && <Card title="Summary written by the customer" icon="ti-notes"><p>{data.business.products_services}</p></Card>}
    </div>
  );
};

// ---------- Subscription & membership ----------
const SubscriptionAction = ({ action, subscription, plans, userId, onDone, onCancel }) => {
  const [reason, setReason] = useState("");
  const [days, setDays] = useState(action === "grant" ? 30 : 7);
  const [planId, setPlanId] = useState("");
  const [saving, setSaving] = useState(false);
  const grantable = plans.filter((plan) => !plan.isFree && !plan.archived);
  const config = {
    "cancel-renewal": { title: "Cancel renewal", button: "Cancel renewal", danger: false, help: "The plan stays active until the current period ends, then the account moves to the free plan. Recurring gateway subscriptions are cancelled at the gateway." },
    cancel: { title: "Cancel subscription now", button: "Cancel immediately", danger: true, help: "Access to paid features ends right away and the account moves to the free plan. No refund is issued; refunds are handled in Payment gateways." },
    extend: { title: "Extend current period", button: "Extend", danger: false, help: "Adds days to the current period at no charge." },
    grant: { title: "Grant a plan", button: "Grant plan", danger: false, help: "Activates a paid plan at no charge for a fixed number of days. It ends on its own; the customer can then renew or upgrade." }
  }[action];

  const submit = async (event) => {
    event.preventDefault();
    if (config.danger && !window.confirm("Cancel this subscription immediately?")) return;
    setSaving(true);
    try {
      const body = { reason };
      if (action === "extend" || action === "grant") body.days = Number(days);
      if (action === "grant") body.planId = Number(planId);
      const result = await apiRequest(`/api/admin/users/${userId}/subscription/${action}`, { method: "POST", body: JSON.stringify(body) });
      showSuccess(result.message);
      await onDone();
    } catch (requestError) {
      showError(requestError.message || "Unable to update the subscription.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className={`admin-management-form quick-view-form quick-view-action-form${config.danger ? " is-danger" : ""}`} onSubmit={submit}>
      <div className="is-wide"><strong>{config.title}{subscription?.plan?.name && action !== "grant" ? ` · ${subscription.plan.name}` : ""}</strong><p className="quick-view-note">{config.help}</p></div>
      {action === "grant" && <label>Plan
        <select value={planId} onChange={(event) => setPlanId(event.target.value)} required disabled={saving}>
          <option value="">Choose a plan</option>
          {grantable.map((plan) => <option key={plan.id} value={plan.id}>{plan.name}{plan.enabled ? "" : " (hidden from customers)"}</option>)}
        </select>
      </label>}
      {(action === "extend" || action === "grant") && <label>Days
        <input type="number" min="1" max={action === "grant" ? 730 : 365} value={days} onChange={(event) => setDays(event.target.value)} required disabled={saving} />
      </label>}
      <label className="is-wide">Reason (kept in the audit log)
        <textarea value={reason} onChange={(event) => setReason(event.target.value)} minLength={5} maxLength={500} required placeholder="e.g. Goodwill credit for the outage on 12 Oct" disabled={saving} />
      </label>
      <div className="admin-management-form-actions">
        <button type="button" className="admin-management-button" onClick={onCancel} disabled={saving}>Back</button>
        <button type="submit" className={`admin-management-button ${config.danger ? "danger" : "primary"}`} disabled={saving}>{saving ? <><Spinner /> Working…</> : config.button}</button>
      </div>
    </form>
  );
};

const SubscriptionTab = ({ data, plans, onReload }) => {
  const { can } = useAdminAccess();
  const canManage = can("admin.users.manage_subscriptions");
  const [action, setAction] = useState(null);
  const current = data.subscriptions.find((item) => ["active", "past_due", "halted"].includes(item.status));
  const done = async () => {
    setAction(null);
    await onReload();
  };
  const actions = current
    ? [
      !current.cancelAtCycleEnd && ["cancel-renewal", "Cancel renewal", "ti-player-pause"],
      current.billingMode === "one_time" && ["extend", "Extend period", "ti-calendar-plus"],
      ["cancel", "Cancel now", "ti-circle-x"]
    ].filter(Boolean)
    : [["grant", "Grant a plan", "ti-gift"]];

  return (
    <div className="quick-view-stack">
      <Card title="Current membership" icon="ti-crown" actions={current && <span className={`admin-status-badge is-${current.status}`}>{formatStatus(current.status)}</span>}>
        {current ? <dl className="admin-management-meta quick-view-meta-grid">
          <Detail label="Plan" value={current.plan?.name} />
          <Detail label="Billing" value={`${formatStatus(current.billingInterval)} · ${current.billingMode === "one_time" ? "Pay per period" : "Auto-renewing"}`} />
          <Detail label="Amount" value={Number(current.totalAmount) > 0 ? money(current.totalAmount, current.currency) : "No charge"} />
          <Detail label="Provider" value={current.provider === "manual" ? "Granted by admin" : formatStatus(current.provider)} />
          <Detail label="Current period" value={`${dateOnly(current.currentPeriodStart)} – ${dateOnly(current.currentPeriodEnd)}`} />
          <Detail label="Renewal" value={current.cancelAtCycleEnd ? "Cancelled — ends at period end" : current.billingMode === "one_time" ? "Customer renews manually" : "Renews automatically"} />
          <Detail label="Member since" value={dateOnly(current.activatedAt || current.createdAt)} />
          {current.scheduledPlanId && <Detail label="Scheduled change" value="A plan change is scheduled for the next period" />}
        </dl> : <p>On the free plan{data.planUsage?.plan?.name ? ` (${data.planUsage.plan.name})` : ""}. No paid subscription is active.</p>}
        {canManage && !action && <div className="quick-view-actions">
          {actions.map(([id, label, icon]) => <button key={id} type="button" className={`admin-management-button${id === "cancel" ? " danger" : ""}`} onClick={() => setAction(id)}><i className={`ti ${icon}`} aria-hidden="true" /> {label}</button>)}
        </div>}
        {canManage && action && <SubscriptionAction action={action} subscription={current} plans={plans} userId={data.user.id} onDone={done} onCancel={() => setAction(null)} />}
        {!canManage && <p className="quick-view-note">Your role can view subscriptions but not change them.</p>}
      </Card>
      <Card title="Subscription history" icon="ti-history">
        {data.subscriptions.length ? <div className="quick-view-table-wrap"><table className="quick-view-table">
          <thead><tr><th>Plan</th><th>Status</th><th>Billing</th><th>Period</th><th>Created</th></tr></thead>
          <tbody>{data.subscriptions.map((item) => <tr key={item.id}>
            <td data-label="Plan">{item.plan?.name || "—"}{item.provider === "manual" && <small>Granted by admin</small>}</td>
            <td data-label="Status"><span className={`admin-status-badge is-${item.status}`}>{formatStatus(item.status)}</span></td>
            <td data-label="Billing">{formatStatus(item.billingInterval)}{Number(item.totalAmount) > 0 ? ` · ${money(item.totalAmount, item.currency)}` : ""}</td>
            <td data-label="Period">{item.currentPeriodStart ? `${dateOnly(item.currentPeriodStart)} – ${dateOnly(item.currentPeriodEnd)}` : "—"}</td>
            <td data-label="Created">{dateOnly(item.createdAt)}</td>
          </tr>)}</tbody>
        </table></div> : <p>No subscriptions have been created for this account.</p>}
      </Card>
    </div>
  );
};

// ---------- Payment history ----------
const PaymentsTab = ({ userId }) => {
  const { can } = useAdminAccess();
  const allowed = can("admin.users.view_payments");
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [group, setGroup] = useState("all");
  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      setState({ loading: false, error: "", data: await apiRequest(`/api/admin/users/${userId}/payments`) });
    } catch (requestError) {
      setState({ loading: false, error: requestError.message || "Unable to load payment history.", data: null });
    }
  }, [userId]);
  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  if (!allowed) return <Empty icon="ti-lock" title="Payment history is restricted">Your role needs the “View payments” permission for Users.</Empty>;
  if (state.loading && !state.data) return <SkeletonRegion label="Loading payment history"><Skeleton height={70} radius={8} /><Skeleton height={180} radius={8} /></SkeletonRegion>;
  if (state.error) return <ErrorNote message={state.error} onRetry={load} />;
  const { payments, counts, totals } = state.data;
  const shown = group === "all" ? payments : payments.filter((payment) => payment.group === group);
  const currencies = Object.keys(totals);
  return (
    <div className="quick-view-stack">
      <div className="quick-view-tiles is-compact">
        {["successful", "pending", "failed"].map((key) => (
          <div className={`quick-view-tile is-static is-${key}`} key={key}>
            <span>{paymentGroupLabels[key]}</span><strong>{counts[key]}</strong>
            <small>{currencies.length ? currencies.map((currency) => money(totals[currency][key], currency)).join(" · ") : "—"}</small>
          </div>
        ))}
      </div>
      <div className="quick-view-segmented" role="group" aria-label="Filter payments">
        {[["all", `All (${payments.length})`], ["successful", "Successful"], ["pending", "Pending"], ["failed", "Failed"]].map(([key, label]) => (
          <button key={key} type="button" aria-pressed={group === key} onClick={() => setGroup(key)}>{label}</button>
        ))}
      </div>
      {shown.length ? <div className="quick-view-table-wrap"><table className="quick-view-table">
        <thead><tr><th>Date</th><th>Reference</th><th>Plan</th><th>Amount</th><th>Status</th><th>Gateway</th></tr></thead>
        <tbody>{shown.map((payment) => <tr key={payment.id}>
          <td data-label="Date">{dateTime(payment.createdAt)}{payment.completedAt && <small>Completed {dateTime(payment.completedAt)}</small>}</td>
          <td data-label="Reference"><code>{payment.reference}</code>{payment.providerPaymentId && payment.providerPaymentId !== payment.reference && <small>Gateway ID <code>{payment.providerPaymentId}</code></small>}</td>
          <td data-label="Plan">{payment.planName || "—"}<small>{formatStatus(payment.purpose)}{payment.billingInterval ? ` · ${payment.billingInterval}` : ""}</small></td>
          <td data-label="Amount"><strong>{money(payment.amount, payment.currency)}</strong>{Number(payment.refundedAmount) > 0 && <small>Refunded {money(payment.refundedAmount, payment.currency)}</small>}</td>
          <td data-label="Status"><span className={`quick-view-pay is-${payment.group}`}>{formatStatus(payment.status)}</span>{payment.failureReason && <small>{payment.failureReason}</small>}</td>
          <td data-label="Gateway">{formatStatus(payment.provider)}{payment.environment === "test" && <small>Test mode</small>}{payment.invoiceUrl && <small><a className="admin-inline-link" href={payment.invoiceUrl} target="_blank" rel="noreferrer">Invoice</a></small>}</td>
        </tr>)}</tbody>
      </table></div> : <Empty icon="ti-receipt" title={payments.length ? "No payments match this filter" : "No payments yet"}>{payments.length ? null : "Checkout attempts and charges will appear here."}</Empty>}
    </div>
  );
};

// ---------- Social accounts ----------
const platformIcons = { facebook: "ti-brand-facebook", instagram: "ti-brand-instagram", linkedin: "ti-brand-linkedin", twitter: "ti-brand-x", x: "ti-brand-x", youtube: "ti-brand-youtube", tiktok: "ti-brand-tiktok", pinterest: "ti-brand-pinterest", threads: "ti-brand-threads", google: "ti-brand-google" };
const SocialTab = ({ data }) => (
  data.socialAccounts.length ? <ul className="quick-view-social">{data.socialAccounts.map((account) => (
    <li key={account.id}>
      <i className={`ti ${platformIcons[String(account.platform).toLowerCase()] || "ti-world"}`} aria-hidden="true" />
      <div><strong>{account.accountName || account.username || "Account"}</strong><small>{formatStatus(account.platform)}{account.username ? ` · @${String(account.username).replace(/^@/, "")}` : ""}</small></div>
      <div><span className={`admin-status-badge is-${String(account.status).toLowerCase() === "connected" ? "active" : "inactive"}`}>{formatStatus(String(account.status).toLowerCase())}</span><small>Synced {account.lastSyncedAt ? relativeTime(account.lastSyncedAt) : "never"} · connected {dateOnly(account.createdAt)}</small></div>
    </li>
  ))}</ul> : <Empty icon="ti-share" title="No social accounts connected" />
);

// ---------- Usage & limits ----------
const UsageTab = ({ data }) => {
  const usage = data.planUsage;
  const formatNumber = (value) => new Intl.NumberFormat().format(Number(value) || 0);
  return (
    <div className="quick-view-stack">
      {usage ? <Card title={`${usage.plan.name} plan limits`} icon="ti-gauge" actions={<small className="quick-view-note">Monthly quotas reset {dateOnly(usage.period?.end)}</small>}>
        <ul className="quick-view-usage">{usage.usage.map((item) => {
          const unlimited = item.limit === null;
          const percent = unlimited || !item.limit ? 0 : Math.min(100, Math.round((item.used / item.limit) * 100));
          return (
            <li key={item.key} className={item.reached ? "is-reached" : percent >= 80 ? "is-warning" : ""}>
              <div><strong>{item.label}</strong><span>{formatNumber(item.used)} / {unlimited ? "Unlimited" : `${formatNumber(item.limit)} ${item.unit}`}</span></div>
              <div className="quick-view-meter" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent} aria-label={item.label}><span style={{ width: `${unlimited ? 0 : percent}%` }} /></div>
              <small>{item.limit === 0 ? "Not included in this plan" : item.reached ? "Limit reached" : unlimited ? "No limit" : `${formatNumber(item.remaining)} remaining${item.resets ? " this period" : ""}`}</small>
            </li>
          );
        })}</ul>
      </Card> : <ErrorNote message="Plan usage couldn't be calculated for this account." />}
      <div className="admin-management-grid">
        <Card title="AI usage (all time)" icon="ti-sparkles">
          {data.aiUsage.length ? <ul className="admin-management-list">{data.aiUsage.map((item) => <li key={item.category}><strong>{formatStatus(item.category)}</strong><span>{formatNumber(item.units)} units</span></li>)}</ul> : <p>No AI usage recorded.</p>}
        </Card>
        <Card title="Included features" icon="ti-list-check">
          {usage ? <ul className="quick-view-features">{Object.entries(usage.entitlements).filter(([, value]) => typeof value === "boolean").map(([key, value]) => (
            <li key={key} className={value ? "is-on" : ""}><i className={`ti ${value ? "ti-circle-check" : "ti-circle-minus"}`} aria-hidden="true" />{formatStatus(key.replace(/([A-Z])/g, " $1")).toLowerCase().replace(/^./, (letter) => letter.toUpperCase())}</li>
          ))}</ul> : <p>—</p>}
        </Card>
      </div>
    </div>
  );
};

// ---------- Activity logs ----------
const describeChanges = (changes) => {
  if (!changes || typeof changes !== "object") return changes ? String(changes) : "";
  return Object.entries(changes).filter(([key]) => !["sessionId", "userAgent", "ip"].includes(key)).map(([key, value]) => {
    if (value && typeof value === "object" && "from" in value && "to" in value) return `${key}: ${display(value.from)} → ${display(value.to)}`;
    return `${key}: ${display(value)}`;
  }).join(" · ");
};
const auditLabels = {
  IMPERSONATION_STARTED: "Signed in as this user",
  IMPERSONATION_ENDED: "Ended support session",
  IMPERSONATION_ACTION: "Action during support session"
};
const ActivityTab = ({ data }) => {
  const [view, setView] = useState("user");
  return (
    <div className="quick-view-stack">
      <div className="quick-view-segmented" role="group" aria-label="Activity type">
        <button type="button" aria-pressed={view === "user"} onClick={() => setView("user")}>User activity ({data.timeline.length})</button>
        <button type="button" aria-pressed={view === "admin"} onClick={() => setView("admin")}>Admin actions ({data.audit.length})</button>
      </div>
      {view === "user" ? (data.timeline.length ? <ol className="quick-view-timeline">{data.timeline.map((item, index) => (
        <li key={`${item.type}-${item.createdAt}-${index}`} className={`is-${item.type}`}><span aria-hidden="true" /><div><strong>{item.title}</strong>{item.detail && <small>{item.detail}</small>}</div><time dateTime={item.createdAt} title={dateTime(item.createdAt)}>{relativeTime(item.createdAt)}</time></li>
      ))}</ol> : <Empty icon="ti-history" title="No activity yet" />)
        : (data.audit.length ? <ol className="quick-view-timeline">{data.audit.map((entry) => (
          <li key={entry.id} className={entry.action.startsWith("IMPERSONATION") ? "is-impersonation" : "is-admin"}><span aria-hidden="true" /><div>
            <strong>{auditLabels[entry.action] || formatStatus(entry.action.toLowerCase()).replace(/^./, (letter) => letter.toUpperCase())}</strong>
            <small>{entry.administrator}{describeChanges(entry.changes) ? ` · ${describeChanges(entry.changes)}` : ""}</small>
          </div><time dateTime={entry.createdAt} title={dateTime(entry.createdAt)}>{relativeTime(entry.createdAt)}</time></li>
        ))}</ol> : <Empty icon="ti-shield-check" title="No admin actions recorded" />)}
    </div>
  );
};

// ---------- Security ----------
const CustomerRoleCard = ({ userId }) => {
  const { can } = useAdminAccess();
  const canAssign = can("admin.access_control.edit");
  const [state, setState] = useState(null);
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setError("");
    try {
      const result = await apiRequest(`/api/admin/access-control/customers/${userId}/role`);
      setState({ role: result.role || null, availableRoles: Array.isArray(result.availableRoles) ? result.availableRoles : [] });
      setSelected(result.role ? String(result.role.id) : "");
    } catch (requestError) {
      setError(requestError.message || "Unable to load this customer's workspace role.");
    }
  }, [userId]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setSaving(true);
    try {
      const result = await apiRequest(`/api/admin/access-control/customers/${userId}/role`, { method: "PUT", body: JSON.stringify({ roleId: selected ? Number(selected) : null }) });
      showSuccess(result.message);
      await load();
    } catch (requestError) {
      showError(requestError.message || "Unable to update this customer's workspace role.");
    } finally {
      setSaving(false);
    }
  };
  const current = state?.role ? String(state.role.id) : "";
  return (
    <Card title="Workspace role" icon="ti-key">
      {error ? <ErrorNote message={error} onRetry={load} /> : !state ? <SkeletonRegion label="Loading workspace role"><Skeleton width="75%" height={11} /><Skeleton height={38} radius={8} /></SkeletonRegion> : <>
        <p>{state.role ? `Limited to the ${state.role.name} role.` : "Full access to every workspace feature their plan includes."}</p>
        <div className="quick-view-inline-form">
          <select aria-label="Workspace role" value={selected} onChange={(event) => setSelected(event.target.value)} disabled={!canAssign || saving}>
            <option value="">Full access (default)</option>
            {state.availableRoles.map((role) => <option key={role.id} value={String(role.id)}>{role.name}</option>)}
          </select>
          {canAssign && <button type="button" className="admin-management-button primary" disabled={saving || selected === current} onClick={save}>{saving ? "Saving…" : "Save"}</button>}
        </div>
      </>}
    </Card>
  );
};

const SecurityTab = ({ data, onLoginAs }) => {
  const { can } = useAdminAccess();
  const { security, user } = data;
  const checks = [
    ["Account status", formatStatus(security.accountStatus), security.accountStatus === "active"],
    ["Email verification", security.emailVerified ? "Verified" : "Not verified", security.emailVerified],
    ["Verification link", security.verificationLinkPending ? "Outstanding link sent" : "None outstanding", !security.verificationLinkPending],
    ["Password reset", security.passwordResetPending ? "Reset link outstanding" : "None outstanding", !security.passwordResetPending],
    ["Account deletion", security.deletionRequest ? `${formatStatus(security.deletionRequest.status)} · requested ${dateOnly(security.deletionRequest.requestedAt)}` : "Not requested", !security.deletionRequest || security.deletionRequest.status !== "pending"]
  ];
  return (
    <div className="quick-view-stack">
      <div className="admin-management-grid">
        <Card title="Security status" icon="ti-shield-check">
          <ul className="quick-view-checks">{checks.map(([label, value, ok]) => (
            <li key={label} className={ok ? "is-ok" : "is-attention"}><i className={`ti ${ok ? "ti-circle-check" : "ti-alert-triangle"}`} aria-hidden="true" /><span>{label}</span><strong>{value}</strong></li>
          ))}</ul>
          <p className="quick-view-note">Passwords, verification links and session tokens are never shown to administrators.</p>
        </Card>
        {can("admin.access_control.view") && <CustomerRoleCard userId={user.id} />}
      </div>
      <Card title="Login as user" icon="ti-user-shield" actions={can("admin.users.impersonate") && <button type="button" className="admin-management-button primary" onClick={onLoginAs} disabled={user.accountStatus !== "active"}><i className="ti ti-login-2" aria-hidden="true" /> Login as user</button>}>
        <p>{can("admin.users.impersonate")
          ? user.accountStatus === "active" ? "Open this customer's workspace to troubleshoot. Sessions last 30 minutes, are recorded with your reason, and can't change the password, billing or delete the account." : "Only active accounts can be accessed."
          : "Your role doesn't include the “Login as user” permission."}</p>
        {security.impersonationSessions.length ? <ul className="admin-management-list">{security.impersonationSessions.map((session) => (
          <li key={session.id}><div><strong>{session.administrator}</strong><small>{session.reason || "No reason recorded"}</small></div><time>{dateTime(session.startedAt)}{session.endedAt ? ` → ${dateTime(session.endedAt)}` : ""}</time></li>
        ))}</ul> : <p className="quick-view-note">No support sessions have been recorded for this user.</p>}
      </Card>
    </div>
  );
};

// ---------- Login as user dialog ----------
const LoginAsDialog = ({ user, onClose }) => {
  const [reason, setReason] = useState("");
  const [acknowledge, setAcknowledge] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const titleId = useId();
  const reasonRef = useRef(null);
  useEffect(() => { reasonRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (event) => { if (event.key === "Escape" && !submitting) { event.stopPropagation(); onClose(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose, submitting]);

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const result = await apiRequest(`/api/admin/users/${user.id}/impersonate`, { method: "POST", body: JSON.stringify({ reason, acknowledge }) });
      beginImpersonation(result);
      // A full page load resets every admin-only state before the customer's workspace opens.
      window.location.assign(result.user.onboardingComplete ? "/dashboard" : "/onboarding");
    } catch (requestError) {
      setError(requestError.message || "Unable to start the support session.");
      setSubmitting(false);
    }
  };

  return (
    <div className="quick-view-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !submitting) onClose(); }}>
      <form className="quick-view-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} onSubmit={submit}>
        <h2 id={titleId}><i className="ti ti-user-shield" aria-hidden="true" /> Login as {displayName(user)}</h2>
        <p>You'll open <strong>{user.email}</strong>'s workspace as them for up to 30 minutes. A banner shows throughout, and you can return to the admin portal at any time.</p>
        <ul className="quick-view-dialog-rules">
          <li><i className="ti ti-eye" aria-hidden="true" /> The start, end and every change you make are recorded under your name.</li>
          <li><i className="ti ti-lock" aria-hidden="true" /> Password, email, billing, social account connections and account deletion are blocked.</li>
          <li><i className="ti ti-key-off" aria-hidden="true" /> The customer's password and sessions are never revealed or changed.</li>
        </ul>
        <label>Reason for access
          <textarea ref={reasonRef} value={reason} onChange={(event) => setReason(event.target.value)} minLength={10} maxLength={500} required placeholder="e.g. Ticket SUP-1042: customer can't see scheduled posts" disabled={submitting} />
        </label>
        <label className="quick-view-dialog-ack">
          <input type="checkbox" checked={acknowledge} onChange={(event) => setAcknowledge(event.target.checked)} required disabled={submitting} />
          <span>I'm authorised to access this customer's account for the reason above.</span>
        </label>
        {error && <p className="quick-view-dialog-error" role="alert">{error}</p>}
        <div className="admin-management-form-actions">
          <button type="button" className="admin-management-button" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="submit" className="admin-management-button primary" disabled={submitting || !acknowledge || reason.trim().length < 10}>{submitting ? <><Spinner /> Starting…</> : <><i className="ti ti-login-2" aria-hidden="true" /> Start support session</>}</button>
        </div>
      </form>
    </div>
  );
};

// ---------- Drawer ----------
const UserQuickView = ({ userId, tab, onTabChange, onClose, plans, latestPayment, onUserChanged }) => {
  const { can } = useAdminAccess();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [loginAs, setLoginAs] = useState(false);
  const panelRef = useRef(null);
  const tabRefs = useRef({});
  const titleId = useId();
  const activeTab = tabIds.includes(tab) ? tab : "overview";
  const request = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++request.current;
    setLoading(true);
    setError("");
    try {
      const result = await apiRequest(`/api/admin/users/${userId}`);
      if (requestId === request.current) setData(result);
    } catch (requestError) {
      if (requestId === request.current) setError(requestError.message || "Unable to load this user.");
    } finally {
      if (requestId === request.current) setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    setData(null);
    load();
    return () => { request.current += 1; };
  }, [load]);

  const reload = useCallback(async () => {
    await load();
    onUserChanged?.();
  }, [load, onUserChanged]);

  // Runs once per open drawer: focus moves in, Escape closes, and focus returns to where it was on close.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const previous = document.activeElement;
    panelRef.current?.focus();
    const onKey = (event) => { if (event.key === "Escape") closeRef.current(); };
    window.addEventListener("keydown", onKey);
    document.body.classList.add("quick-view-open");
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.classList.remove("quick-view-open");
      if (previous && typeof previous.focus === "function") previous.focus();
    };
  }, []);

  const onTabKey = (event) => {
    const index = tabIds.indexOf(activeTab);
    const next = { ArrowRight: index + 1, ArrowDown: index + 1, ArrowLeft: index - 1, ArrowUp: index - 1, Home: 0, End: tabIds.length - 1 }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    const id = tabIds[(next + tabIds.length) % tabIds.length];
    onTabChange(id);
    tabRefs.current[id]?.focus();
  };

  const user = data?.user;
  const name = user ? displayName(user) : "User";
  // Portalled to <body> so page entrance animations (which transform the page's children) can't shift the drawer.
  return createPortal(
    <div className="quick-view-root">
      <button type="button" className="quick-view-scrim" aria-label="Close quick view" onClick={onClose} tabIndex={-1} />
      <aside ref={panelRef} className="quick-view-panel" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <header className="quick-view-header">
          <div className="quick-view-identity">
            <span className="admin-detail-avatar" aria-hidden="true">{user ? name.slice(0, 1).toUpperCase() : <i className="ti ti-user" />}</span>
            <div>
              <h2 id={titleId}>{user ? name : loading ? "Loading user…" : "User"}</h2>
              {user && <p>{user.email}</p>}
              {user && <div className="quick-view-badges">
                <span className={`admin-status-badge is-${user.accountStatus}`}>{user.accountStatus}</span>
                <span className={`admin-user-verified${user.isVerified ? " is-verified" : ""}`}>{user.isVerified ? "Verified" : "Unverified"}</span>
                {data.planUsage && <span className="admin-user-role">{data.planUsage.plan.name}</span>}
                <span className="quick-view-muted">Joined {dateOnly(user.createdAt)}</span>
              </div>}
            </div>
          </div>
          <div className="quick-view-header-actions">
            {user && can("admin.users.impersonate") && <button type="button" className="admin-management-button primary" onClick={() => setLoginAs(true)} disabled={user.accountStatus !== "active"} aria-label="Login as user" title={user.accountStatus !== "active" ? "Only active accounts can be accessed" : "Login as user"}>
              <i className="ti ti-login-2" aria-hidden="true" /> <span>Login as user</span>
            </button>}
            <button type="button" className="quick-view-icon-button" onClick={load} disabled={loading} aria-label="Refresh"><i className={`ti ti-refresh${loading && data ? " is-spinning" : ""}`} aria-hidden="true" /></button>
            <button type="button" className="quick-view-icon-button" onClick={onClose} aria-label="Close quick view"><i className="ti ti-x" aria-hidden="true" /></button>
          </div>
        </header>

        <nav className="quick-view-tabs" role="tablist" aria-label="User sections" onKeyDown={onTabKey}>
          {quickViewTabs.map(([id, label, icon]) => (
            <button key={id} ref={(node) => { tabRefs.current[id] = node; }} type="button" role="tab" id={`quick-view-tab-${id}`} aria-selected={activeTab === id} aria-controls="quick-view-tabpanel" tabIndex={activeTab === id ? 0 : -1} onClick={() => onTabChange(id)}>
              <i className={`ti ${icon}`} aria-hidden="true" /> {label}
            </button>
          ))}
        </nav>

        <div className="quick-view-body" id="quick-view-tabpanel" role="tabpanel" aria-labelledby={`quick-view-tab-${activeTab}`}>
          {error && <ErrorNote message={error} onRetry={load} />}
          {!data && loading && <SkeletonRegion label="Loading user details"><Skeleton height={86} radius={10} /><Skeleton height={160} radius={10} /><Skeleton height={120} radius={10} /></SkeletonRegion>}
          {data && <>
            {activeTab === "overview" && <OverviewTab data={data} latestPayment={latestPayment} canViewPayments={can("admin.users.view_payments")} onReload={reload} onOpenTab={onTabChange} />}
            {activeTab === "personal" && <PersonalTab data={data} onReload={reload} />}
            {activeTab === "business" && <BusinessTab data={data} onReload={reload} />}
            {activeTab === "products" && <ProductsTab data={data} />}
            {activeTab === "subscription" && <SubscriptionTab data={data} plans={plans} onReload={reload} />}
            {activeTab === "payments" && <PaymentsTab userId={data.user.id} />}
            {activeTab === "social" && <SocialTab data={data} />}
            {activeTab === "usage" && <UsageTab data={data} />}
            {activeTab === "activity" && <ActivityTab data={data} />}
            {activeTab === "security" && <SecurityTab data={data} onLoginAs={() => setLoginAs(true)} />}
          </>}
        </div>
      </aside>
      {loginAs && user && <LoginAsDialog user={user} onClose={() => setLoginAs(false)} />}
    </div>,
    document.body
  );
};

export default UserQuickView;
